import type Phaser from "phaser";
import type { SimEvent } from "../sim";
import type { SongCursor } from "./songs";

/** Pitch per light colour in cents: a pentatonic scale, so any run of plings sounds nice. */
export const COLOR_DETUNE: readonly number[] = [0, 200, 400, 700, 900, 1200];

type Cue = { key: string; volume?: number; detune?: number };

/**
 * Which sounds a step's events make (pure apart from moving the song on, unit-tested). With a
 * song, a collect plays its next note (gold adds a quiet sparkle) instead of the colour's pling.
 */
export function soundsForEvents(
	events: readonly SimEvent[],
	song: SongCursor | null = null,
): Cue[] {
	const cues: Cue[] = [];
	for (const ev of events) {
		if (ev.type === "started") cues.push({ key: "go" });
		else if (ev.type === "lit") cues.push({ key: "on", volume: 0.25 });
		else if (ev.type === "collected" && song) {
			cues.push({ key: "pling", detune: song.next() });
			if (ev.light.golden) cues.push({ key: "golden", volume: 0.3 });
		} else if (ev.type === "collected") {
			cues.push(
				ev.light.golden
					? { key: "golden" }
					: { key: "pling", detune: COLOR_DETUNE[ev.light.color] ?? 0 },
			);
		} else if (ev.type === "batCame") cues.push({ key: "bat", volume: 0.45 });
		else if (ev.type === "batCaught") cues.push({ key: "golden" });
		else if (ev.type === "ghostCame") cues.push({ key: "ghost", volume: 0.45 });
		else if (ev.type === "ghostCaught") cues.push({ key: "golden" });
		else if (ev.type === "tricked") cues.push({ key: "trick" });
		else if (ev.type === "streak") cues.push({ key: "streak", volume: 0.6 });
		else if (ev.type === "lifeLost") cues.push({ key: "miss", volume: 0.5 });
		else if (ev.type === "missed") {
			if (ev.penalty > 0) cues.push({ key: "miss", volume: 0.5 });
		} else if (ev.type === "timeUp") cues.push({ key: "end" });
	}
	return cues;
}

/**
 * Plays sound effects. Without Web Audio the loader skips sounds, so missing keys are ignored;
 * muting is `scene.sound.mute` (the menu's switch).
 */
export class Sfx {
	constructor(private readonly scene: Phaser.Scene) {}

	play(cue: Cue): void {
		if (!this.scene.cache.audio.exists(cue.key)) return;
		this.scene.sound.play(cue.key, { volume: cue.volume ?? 0.7, detune: cue.detune ?? 0 });
	}

	events(events: readonly SimEvent[], song: SongCursor | null = null): void {
		for (const cue of soundsForEvents(events, song)) this.play(cue);
	}
}
